import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ErrorEntity } from '@p2p/shared/domain';
import { transformStringPath } from '@p2p/shared/utils';
import _ from 'lodash';

import { CommonSettingsErrors, CommonSettingsKeys, PaymentMethodSettingsKeys } from '../constants';
import {
    BalanceCommonSettings,
    CommonSettings,
    CommonSettingsUnionKeys,
    OrderPriorityCommonSettings,
    PaymentMethodSetting,
    TeamLeadCommissionCommonSettings
} from '../entities';
import { PlatformSettingsFacade } from '../state/platform-settings.facade';

export enum PaymentMethodsFormControls {
    PaymentMethodSettings = 'paymentMethodSettings',
    PaymentMethodId = 'paymentMethodId',
    Role = 'role'
}

@Injectable()
export class PlatformSettingsFormService {
    private readonly platformSettingsFacade = inject(PlatformSettingsFacade);

    readonly commonForm = new FormGroup({
        [CommonSettingsKeys.MinTrustBalance]: new FormControl('', [Validators.required]),
        [CommonSettingsKeys.MinTradeBalance]: new FormControl('', [Validators.required]),
        [CommonSettingsKeys.TeamLeadPayinCommissionRate]: new FormControl('', [Validators.required]),
        [CommonSettingsKeys.TeamLeadPayoutCommissionRate]: new FormControl('', [Validators.required]),
        [CommonSettingsKeys.TraderOrderShare]: new FormControl('', [Validators.required]),
        [CommonSettingsKeys.NewTraderOrderShare]: new FormControl('', [Validators.required]),
        [CommonSettingsKeys.OperatorOrderShare]: new FormControl('', [Validators.required])
    });
    readonly paymentMethodsFormArray = new FormArray<any>([]);
    readonly paymentMethodsForm = new FormGroup({
        [PaymentMethodsFormControls.PaymentMethodSettings]: this.paymentMethodsFormArray
    });

    setCommonSettings(commonSettings: CommonSettings | null): void {
        if (commonSettings) {
            _.keys(this.commonForm.controls).forEach((key) => {
                const settings: OrderPriorityCommonSettings & TeamLeadCommissionCommonSettings & BalanceCommonSettings =
                    {
                        ...commonSettings.balanceSettings,
                        ...commonSettings.orderPrioritySettings,
                        ...commonSettings.teamLeadCommissionSettings
                    };
                const control = this.commonForm.get(key as CommonSettingsKeys);

                control?.setValue(
                    settings[
                        key as unknown as keyof (OrderPriorityCommonSettings &
                            TeamLeadCommissionCommonSettings &
                            BalanceCommonSettings)
                    ],
                    { emitEvent: false }
                );
            });
        }
    }

    setPaymentMethodSettings(paymentMethodSettings: PaymentMethodSetting[]): void {
        paymentMethodSettings.forEach((paymentMethodSetting) => {
            const formArray = this.paymentMethodsForm.get(
                PaymentMethodsFormControls.PaymentMethodSettings
            ) as FormArray;
            formArray.push(
                new FormGroup({
                    [PaymentMethodsFormControls.PaymentMethodId]: new FormControl(
                        paymentMethodSetting.paymentMethodId,
                        [Validators.required]
                    ),
                    [PaymentMethodsFormControls.Role]: new FormControl(paymentMethodSetting.role, [
                        Validators.required
                    ]),
                    [PaymentMethodSettingsKeys.DailyLimit]: new FormControl(
                        paymentMethodSetting[PaymentMethodSettingsKeys.DailyLimit],
                        [Validators.required]
                    ),
                    [PaymentMethodSettingsKeys.ThresholdOrderAmount]: new FormControl(
                        paymentMethodSetting[PaymentMethodSettingsKeys.ThresholdOrderAmount],
                        [Validators.required]
                    ),
                    [PaymentMethodSettingsKeys.UpToThresholdCommissionRate]: new FormControl(
                        paymentMethodSetting[PaymentMethodSettingsKeys.UpToThresholdCommissionRate],
                        [Validators.required]
                    ),
                    [PaymentMethodSettingsKeys.EqualOrAboveThresholdCommissionRate]: new FormControl(
                        paymentMethodSetting[PaymentMethodSettingsKeys.EqualOrAboveThresholdCommissionRate],
                        [Validators.required]
                    )
                })
            );
        });
    }

    validateCommonForm(): void {
        _.values(this.commonForm.controls).forEach(this.markForValidation);
        this.markForValidation(this.commonForm);
    }

    enableCommonForm(enable: boolean): void {
        _.values(this.commonForm.controls).forEach((control) => {
            if (enable) {
                control.enable({ emitEvent: false });
            } else {
                control.disable({ emitEvent: false });
            }
        });
    }

    validatePaymentMethodsForm(): void {
        this.paymentMethodsFormArray.controls.forEach((formGroup) => {
            _.values((formGroup as FormGroup).controls).forEach(this.markForValidation);
            this.markForValidation(formGroup);
        });
        this.markForValidation(this.paymentMethodsFormArray);
        this.markForValidation(this.paymentMethodsForm);
    }

    enablePaymentMethodsForm(enable: boolean): void {
        this.paymentMethodsFormArray.controls.forEach((formGroup) => {
            _.values((formGroup as FormGroup).controls).forEach((control) => {
                if (enable) {
                    control.enable({ emitEvent: false });
                } else {
                    control.disable({ emitEvent: false });
                }
            });
        });
    }

    handleCommonSettingsHttpErrors(errors: ErrorEntity[]): void {
        if (!errors.length) {
            return;
        }

        const uniqErrors = _.uniqBy(errors, 'field');

        uniqErrors.forEach((error) => {
            const field = error.field as CommonSettingsKeys;

            if (error.code) {
                this.commonForm.get(field)?.setErrors({ [error.code]: true });
            }
        });
    }

    handlePaymentMethodSettingsHttpErrors(errors: ErrorEntity[]): void {
        if (!errors.length) {
            return;
        }

        const uniqErrors = _.uniqBy(errors, 'field');

        uniqErrors.forEach((error) => {
            const transformedFieldPath = transformStringPath(error.field ?? '');

            this.processPaymentMethodSettingsFieldErrors(this.paymentMethodsForm, transformedFieldPath, error.code);
        });
    }

    private processPaymentMethodSettingsFieldErrors(control: AbstractControl, path: string[], code: string, idx = 0) {
        const innerControl = control.get(path[idx]);

        if (!innerControl) {
            control.setErrors({ [code]: true });

            return;
        }

        this.processPaymentMethodSettingsFieldErrors(innerControl, path, code, idx + 1);
    }

    private markForValidation(control: AbstractControl): void {
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity();
    }
}
